// Ortak personel düzenleme oturumu (#29). Çekmecenin SAHİBİ (liste sayfası / izin paneli) çağırır ve
// `editor`'ı `PersonnelSheet`'e geçer: RHF + zod + kaydetme mutasyonu + iki vazgeç koruması burada,
// wire gövdesi ve önbellek işi portalın `PersonnelClient`ında (`VehicleFormSheet`/`VehicleClient` emsali).
//
// **Değerler tıklama anındaki satırdan (F2).** `begin(target)` formu o satırla sıfırlar; mount-time
// varsayılan yoktur, `isActive` yalnız `target.isActive`'ten gelir. ⋮ "Düzenle" `begin` + çekmeceyi
// açmayı AYNI olayda yapar → çekmecenin ilk karesi zaten düzenleme kipidir (görünüm karesi, belge
// isteği yok). T.C. formda ASLA sunucu değeriyle tutulmaz: kilitliyse alan yok, açıksa boş başlar.
//
// **İki çıkış.** `leaveEdit` yalnız kipten çıkar (başarı bildirimi kalır; iki korumanın `onClose`'u).
// `leave` bildirimi ve mutasyonu da temizler — çekmece dışarıdan kapandığında `PersonnelSheet` çağırır.

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { hasErrorCode } from '../api/errors';
import { resolveApiErrorMessage } from '../i18n/apiError';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { useDiscardGuard } from '../ui/useDiscardGuard';
import { PersonnelEditBody } from './PersonnelEditBody';
import {
  type PersonnelChange,
  type PersonnelClient,
  type PersonnelEditTarget,
  isNameRequired,
  tcChange,
  tcLock,
} from './personnelEdit';
import { isValidTcKimlikNo } from './tcKimlik';

/** `SupplierUser.FirstName/LastName` `HasMaxLength(100)`; daha uzun ad PUT'ta 400 döner. */
export const NAME_MAX = 100;

/** Kayıtlı ad doluysa zorunlu; boşsa boş bırakılabilir (`isNameRequired`, #29 I-1). */
export function nameSchema(required: boolean, requiredMessage: string, tooLongMessage: string) {
  const name = z.string().trim().max(NAME_MAX, tooLongMessage);
  return required ? name.min(1, requiredMessage) : name;
}

function personnelFormSchema(t: TFunction, required: { firstName: boolean; lastName: boolean }) {
  return z.object({
    firstName: nameSchema(
      required.firstName,
      t('personnelForm.validation.firstNameRequired'),
      t('personnelForm.validation.firstNameTooLong', { max: NAME_MAX }),
    ),
    lastName: nameSchema(
      required.lastName,
      t('personnelForm.validation.lastNameRequired'),
      t('personnelForm.validation.lastNameTooLong', { max: NAME_MAX }),
    ),
    tcKimlikNo: z
      .string()
      .trim()
      .refine((v) => v === '' || isValidTcKimlikNo(v), t('personnelForm.validation.tcInvalid')),
    accessLevel: z.enum(['Personeli', 'Yetkili']),
    isActive: z.boolean(),
  });
}

export type PersonnelFormValues = z.infer<ReturnType<typeof personnelFormSchema>>;

function valuesOf(target: PersonnelEditTarget): PersonnelFormValues {
  return {
    firstName: target.firstName,
    lastName: target.lastName,
    tcKimlikNo: '',
    accessLevel: target.accessLevel,
    isActive: target.isActive,
  };
}

/** T.C. girdisine bağlanan sunucu kodları → yerel yedek anahtar; diğer her kod form düzeyi Alert. */
function tcFieldFallbackKey(error: unknown): string | null {
  if (hasErrorCode(error, 'TC_KIMLIK_INVALID')) return 'personnelForm.validation.tcInvalid';
  if (hasErrorCode(error, 'PERSONNEL_TC_ALREADY_EXISTS')) {
    return 'personnelForm.validation.tcExistsFallback';
  }
  return null;
}

/** Şemadan boş ad yalnız kayıtlı ad boşken geçer → `null` (= değişmez); PUT'a asla `""` gitmez. */
function nameOrUnchanged(value: string): string | null {
  const v = value.trim();
  return v.length > 0 ? v : null;
}

function sameUuid(a: string, b: string): boolean {
  // Büyük/küçük harf duyarsız (D21): uuid'ler farklı kaynaklardan (liste, izin satırı, gezinme) gelir.
  return a.toLowerCase() === b.toLowerCase();
}

interface SaveVariables {
  uuid: string;
  change: PersonnelChange;
  /** Gönderim anında T.C. girdisi vardı — yalnız o zaman T.C. kodları alana bağlanır. */
  tcEditable: boolean;
  /** Gönderen düzenleme oturumu — sonuç yalnız hâlâ o oturumdaysak duruma yazılır. */
  session: number;
}

export interface UsePersonnelEditorOptions {
  client: PersonnelClient;
  /** Closes the whole drawer (the owner's onOpenChange(false) / state reset). */
  onCloseDrawer: () => void;
}

export interface PersonnelEditor {
  /** Stable. Called from click handlers (Düzenle button, row ⋮) with the row in hand. */
  begin: (target: PersonnelEditTarget) => void;
  isEditing: (userUuid: string | null) => boolean;
  /** Exit edit mode AND clear the success notice. Stable; used when the drawer closes externally. */
  leave: () => void;
  busy: boolean;
  /** The form — rendered by `PersonnelSheet` in edit mode. */
  body: ReactNode;
  /** Vazgeç + Kaydet (primary). */
  footer: ReactNode;
  /** ✕ / Esc / outside click while editing. */
  requestCloseDrawer: () => void;
  /** Success Alert ("Bilgiler güncellendi.") until the next begin/leave, else null. */
  notice: (userUuid: string) => ReactNode;
  discardDialogs: ReactNode;
  editButtonRef: RefObject<HTMLButtonElement | null>;
}

export function usePersonnelEditor({
  client,
  onCloseDrawer,
}: UsePersonnelEditorOptions): PersonnelEditor {
  const { t } = useTranslation();
  const [target, setTarget] = useState<PersonnelEditTarget | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [focusTick, setFocusTick] = useState(0);
  const editButtonRef = useRef<HTMLButtonElement | null>(null);
  // Her `begin`/`leave` yeni bir oturum açar. Terk edilmiş bir kaydetme (çekmece dışarıdan kapandı,
  // başka kişi açıldı) yine önbelleği tazeler ama yeni oturumun durumuna (bildirim, kip, odak, alan
  // hatası) dokunmaz. `inflight` gönderimi süren oturumun numarasıdır: çift gönderimi yalnız o oturumda
  // keser, eski bir kaydetme yeni oturumun Kaydet'ini ölü bırakamaz ve settle'ı yalnız kendi bayrağını
  // indirir. Sheet `busy` iken kullanıcı kapatamaz; oturumu yalnız programatik bir kapanış değiştirir.
  const session = useRef(0);
  const inflight = useRef<number | null>(null);

  // Ad zorunluluğu hedefe bağlıdır. RHF `resolver`'ı her render'ın seçeneklerinden okur
  // (`control._options` her render yenilenir) → `begin` sonrası ilk render'dan itibaren kural
  // hedefi izler; Kaydet ancak o render'dan sonra var olur.
  const firstNameRequired = target !== null && isNameRequired(target.firstName);
  const lastNameRequired = target !== null && isNameRequired(target.lastName);
  const schema = useMemo(
    () => personnelFormSchema(t, { firstName: firstNameRequired, lastName: lastNameRequired }),
    [t, firstNameRequired, lastNameRequired],
  );
  const form = useForm<PersonnelFormValues>({ resolver: zodResolver(schema) });
  const { reset: resetForm, setError } = form;

  const mutation = useMutation({
    mutationFn: ({ uuid, change }: SaveVariables) => client.save(uuid, change),
    onSuccess: (updated, variables) => {
      client.afterSave(updated);
      if (variables.session !== session.current) {
        return;
      }
      setSaved(updated.uuid);
      setTarget(null); // leaveEdit — `leave` DEĞİL: bildirim kalmalı
      setFocusTick((n) => n + 1);
    },
    onError: (error, variables) => {
      client.afterError?.(error);
      if (variables.session !== session.current) {
        return;
      }
      const fallbackKey = variables.tcEditable ? tcFieldFallbackKey(error) : null;
      if (fallbackKey) {
        setError(
          'tcKimlikNo',
          { type: 'server', message: resolveApiErrorMessage(error, t, fallbackKey) },
          { shouldFocus: true },
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      if (inflight.current === variables.session) {
        inflight.current = null;
      }
    },
  });
  const { reset: resetMutation } = mutation;

  const begin = useCallback(
    (next: PersonnelEditTarget) => {
      session.current += 1;
      inflight.current = null;
      resetForm(valuesOf(next));
      resetMutation(); // eski kaydetmeden kopar → yeni oturum `busy` değil
      setSaved(null);
      setTarget(next);
    },
    [resetForm, resetMutation],
  );

  const leave = useCallback(() => {
    session.current += 1;
    inflight.current = null;
    setTarget(null);
    setSaved(null);
    resetMutation();
  }, [resetMutation]);

  // Kaydet ve onaylı vazgeç sonrası odak Düzenle'ye: odağı taşıyan footer düğmesi artık yok.
  useEffect(() => {
    if (focusTick > 0) {
      editButtonRef.current?.focus();
    }
  }, [focusTick]);

  const busy = mutation.isPending;
  // Render sırasında okunur (useDiscardGuard notu): handler'da ilk kez okunan isDirty hep false.
  const dirty = form.formState.isDirty;
  const cancelGuard = useDiscardGuard({
    dirty,
    busy,
    onClose: () => {
      setTarget(null);
      setFocusTick((n) => n + 1);
    },
    // Footer "Vazgeç" görünüm kipine döner; çekmece açık kalır → "kapatılsın mı?" yanlış olurdu.
    labels: {
      title: t('personnelForm.discardEdit.title'),
      body: t('personnelForm.discardEdit.body'),
      cancel: t('personnelForm.discardEdit.cancel'),
      confirm: t('personnelForm.discardEdit.confirm'),
    },
  });
  const closeGuard = useDiscardGuard({
    dirty,
    busy,
    onClose: () => {
      setTarget(null);
      onCloseDrawer();
    },
  });

  const submit = form.handleSubmit((values) => {
    // Eşzamanlı çift Enter/tık: RHF doğrulaması async → senkron ref ikinci gönderimi keser.
    if (target === null || inflight.current !== null || mutation.isPending) {
      return;
    }
    inflight.current = session.current;
    mutation.mutate({
      uuid: target.uuid,
      session: session.current,
      tcEditable: tcLock(target) === 'none',
      change: {
        firstName: nameOrUnchanged(values.firstName),
        lastName: nameOrUnchanged(values.lastName),
        accessLevel: values.accessLevel,
        isActive: values.isActive,
        tc: tcChange(target, values.tcKimlikNo),
      },
    });
  });

  const fieldHandled =
    mutation.variables?.tcEditable === true && tcFieldFallbackKey(mutation.error) !== null;
  const generalError = mutation.isError && !fieldHandled ? mutation.error : null;

  return {
    begin,
    isEditing: (userUuid) =>
      target !== null && userUuid !== null && sameUuid(target.uuid, userUuid),
    leave,
    busy,
    body: target ? (
      <PersonnelEditBody
        key={target.uuid}
        form={form}
        target={target}
        onSubmit={submit}
        generalError={generalError}
      />
    ) : null,
    footer: (
      <>
        <Button variant="secondary" onClick={cancelGuard.requestClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" form="personnel-edit-form" loading={busy} disabled={busy}>
          {t('personnelForm.submit')}
        </Button>
      </>
    ),
    requestCloseDrawer: closeGuard.requestClose,
    // Alert success = role="status": kaydın sonucu gezinmesiz görünür, canlı bölge duyurur.
    notice: (userUuid) =>
      saved !== null && sameUuid(saved, userUuid) ? (
        <Alert tone="success">{t('personnelForm.saved')}</Alert>
      ) : null,
    discardDialogs: (
      <>
        {cancelGuard.discardDialog}
        {closeGuard.discardDialog}
      </>
    ),
    editButtonRef,
  };
}
